import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";

import Style from "@client/utils/Style";

import SearchButton from "../SearchButton";
import StatusIcon from "../StatusIcon";
import TextInput from "../TextInput";

const Input = styled(TextInput)<{ loading?: boolean }>`
  padding-left: 32px;
  ${props =>
    props.loading || (props.showClose && props.value)
      ? "padding-right: 32px;"
      : ""}
  width: 50px;
  opacity: 0;
  transition: all ${Style.animations.swift};
`;
const InputAnimation = (props: any) => css`
  ${Input} {
    width: ${props.width};
    opacity: 1;
  }
`;
const Wrapper = styled.div<{ open?: boolean; width?: string }>`
  position: relative;
  width: ${props => (props.open ? props.width : "50px")};
  ${props => (props.open ? InputAnimation(props) : "")}
`;
const SearchButtonStyled = styled(SearchButton)`
  position: absolute;
  top: 8px;
  left: 8px;
`;
const StatusIconStyled = styled(StatusIcon)`
  position: absolute;
  right: 8px;
  top: 8px;
`;

type Props = {
  onChange?: (value: string) => void;
  onCloseClick?: () => void;
  alwaysOpen?: boolean;
  loading?: boolean;
  focusOnMount?: boolean;
  disablePrimary?: boolean;
  useFilterIcon?: boolean;
  placeholder?: string;
  width?: string;
  value?: string;
  className?: string;
};
type State = {
  open: boolean;
  hover?: boolean;
  focus: boolean;
};
@observer
class SearchInput extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    placeholder: "Search",
    width: `${Style.inputSizes.regular.width}px`,
    value: "",
  };

  state: State = {
    open: false,
    focus: false,
  };

  input: HTMLElement | null = null;
  itemMouseDown = false;

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);

    if (this.props.focusOnMount) {
      this.input && this.input.focus();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      this.setState({ open: false });
    }
  }

  handleSearchButtonClick() {
    this.input && this.input.focus();
    this.setState({ open: !this.state.open });
  }

  handleMouseEnter() {
    this.setState({ hover: true });
  }

  handleMouseLeave() {
    this.setState({ hover: false });
  }

  handleFocus() {
    this.setState({ focus: true });
  }

  handleBlur() {
    this.setState({ focus: false });
  }

  render() {
    return (
      <Wrapper
        open={
          this.state.open || this.props.alwaysOpen || this.props.value !== ""
        }
        onMouseDown={() => {
          this.itemMouseDown = true;
        }}
        onMouseUp={() => {
          this.itemMouseDown = false;
        }}
        onMouseEnter={() => {
          this.handleMouseEnter();
        }}
        onMouseLeave={() => {
          this.handleMouseLeave();
        }}
        width={this.props.width}
        className={this.props.className}
      >
        <Input
          _ref={input => {
            this.input = input;
          }}
          placeholder={this.props.placeholder || ""}
          onChange={val => {
            if (this.props.onChange) {
              this.props.onChange(val);
            }
          }}
          onFocus={() => {
            this.handleFocus();
          }}
          onBlur={() => {
            this.handleBlur();
          }}
          loading={this.props.loading}
          value={this.props.value || ""}
          disablePrimary={this.props.disablePrimary || false}
          showClose={
            !this.props.loading &&
            (this.state.open ||
              this.props.alwaysOpen ||
              this.props.value !== "")
          }
          onCloseClick={() => {
            if (this.props.onCloseClick) {
              this.props.onCloseClick();
            }
          }}
        />
        <SearchButtonStyled
          primary={
            this.state.open ||
            (this.props.alwaysOpen && (this.state.hover || this.state.focus)) ||
            (this.props.value !== "" && (this.state.hover || this.state.focus))
          }
          onClick={() => {
            this.handleSearchButtonClick();
          }}
          useFilterIcon={this.props.useFilterIcon}
        />
        {this.props.loading ? <StatusIconStyled status="RUNNING" /> : null}
      </Wrapper>
    );
  }
}

export default SearchInput;
